import { z } from 'zod';

import { MatchingRecord } from '@eluve/utils';

import { VendorTypesEnum } from './types';

type VendorIntegrationTypes = 'API' | 'WEB';

export const VendorTypesLookup = {
  JANE: 'JANE',
  EXPERITY: 'EXPERITY',
  TEBRA: 'TEBRA',
  MOVEMENT_X: 'MOVEMENT_X',
  BLVD: 'BLVD',
  SILOAM: 'SILOAM',
} as const satisfies MatchingRecord<VendorTypesEnum>;

export const ExternalEhrNoteScoringVendorTypesLookup = {
  SILOAM: 'SILOAM',
} as const satisfies Partial<MatchingRecord<VendorTypesEnum>>;

export const supportedVendorsSchema = z.nativeEnum(VendorTypesLookup);

export const externalEhrNoteScoringVendorsSchema = z.nativeEnum(
  ExternalEhrNoteScoringVendorTypesLookup,
);

export type SupportedVendors = z.infer<typeof supportedVendorsSchema>;

export const VendorIntegrationMapping: Record<
  VendorTypesEnum,
  VendorIntegrationTypes
> = {
  JANE: 'WEB',
  EXPERITY: 'WEB',
  TEBRA: 'WEB',
  MOVEMENT_X: 'WEB',
  BLVD: 'API',
  SILOAM: 'API',
} as const satisfies Record<VendorTypesEnum, VendorIntegrationTypes>;

export const supportedApiBasedVendorsSchema = z.enum(['BLVD', 'SILOAM']);
export const supportedWebBasedVendorsSchema = z.enum([
  'JANE',
  'EXPERITY',
  'MOVEMENT_X',
  'TEBRA',
  'SILOAM',
]);

export type WebBasedSupportedVendors = z.infer<
  typeof supportedWebBasedVendorsSchema
>;
export type ApiBasedSupportedVendors = z.infer<
  typeof supportedApiBasedVendorsSchema
>;

export const isVendorWebBased = (
  vendor: VendorTypesEnum | string,
): vendor is WebBasedSupportedVendors =>
  VendorIntegrationMapping[vendor as VendorTypesEnum] === 'WEB';

export const isVendorApiBased = (
  vendor: VendorTypesEnum | string,
): vendor is WebBasedSupportedVendors =>
  VendorIntegrationMapping[vendor as VendorTypesEnum] === 'API';

export const webhookEventTaskSchema = z.object({
  event: z.string(),
  data: z.record(z.string(), z.unknown()),
  metadata: z.object({
    vendor: supportedApiBasedVendorsSchema,
    domain: z.string(),
    externalEhrId: z.string(),
    tenantId: z.string(),
  }),
});

export type WebhookEventTask = z.infer<typeof webhookEventTaskSchema>;

export type TypedWebhookEventTask<T extends Record<string, unknown>> = Omit<
  WebhookEventTask,
  'data'
> & { data: T };
